import React, { memo, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Dropdown, List } from 'tea-component';
import { useIntl } from 'umi';
import { BubbleProps } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import StartsSvg from '@/pages/datalab/assets/svg/starts.svg';
import CardIconButton from '@/pages/datalab/components/ChartCardIconButtons/CardIconButton';
import type { IAiPromptOption } from '@/pages/datalab/components/DashboardLayout/components/AiAnalysis/types';
import './index.scss';

export interface IAiAnalysisApproachIconProps {
  options: IAiPromptOption[];
  activePromptId: string;
  isCustomActive: boolean;
  bubbleProps?: BubbleProps;
  onEdit: (promptId: string) => void;
  onSelect?: (promptId: string) => void;
}

const AiAnalysisApproachIcon: React.FC<IAiAnalysisApproachIconProps> = (props) => {
  const {
    options,
    activePromptId,
    isCustomActive,
    bubbleProps,
    onEdit,
    onSelect,
  } = props;
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const activeValue = useMemo<string>(() => {
    if (isCustomActive) {
      return '__custom__';
    }
    return String(activePromptId ?? '');
  }, [activePromptId, isCustomActive]);
  // popupContainer 和 positionFixed的设置是为了全屏模式下，dropdown 的定位正确
  return (
    <div ref={ref}>
    <Dropdown
      appearance="pure"
      placement="bottom-end"
      clickClose
      onOpenChange={setOpen}
      overlayStyle={{ zIndex: 99999999 }}
      boxClassName="dbl-ai-analysis-approach-dropdown"
      button={(
        <CardIconButton 
          active={open} 
          bubbleProps={{
            content: formatMessage({ id: 'dbl_common_edit' }),
            ...bubbleProps,
          }}
          onClick={() => { setOpen(true) }}
        >
          <img src={StartsSvg} alt="" aria-hidden />
        </CardIconButton>
      )}
      popupContainer={ref.current || undefined}
      positionFixed={false}
    >
      {(close) => (
        <div className="dbl-ai-analysis-approach-dropdown__panel">
          <div className="dbl-ai-analysis-approach-dropdown__header">
            {formatMessage({ id: 'dbl_ai_analysis_settings_approach_label' })}
          </div>
          <List type="option" className="dbl-ai-analysis-approach-dropdown__list">
            {options.map((item) => {
              const isActive = String(item?.value ?? '') === activeValue;
              return (
                <List.Item
                  key={String(item?.value ?? '')}
                  className={classNames('dbl-ai-analysis-approach-dropdown__list-item', {
                    'dbl-ai-analysis-approach-dropdown__item--active': isActive,
                  })}
                  onClick={() => {
                    onSelect?.(String(item?.value ?? ''));
                    close?.();
                  }}
                >
                  <div className="dbl-ai-analysis-approach-dropdown__item">
                    <span className="dbl-ai-analysis-approach-dropdown__item-text">{item?.text ?? ''}</span>
                    <span
                      className="dbl-ai-analysis-approach-dropdown__item-edit"
                      onClick={(e) => {
                        e?.stopPropagation?.();
                        close?.();
                        // 注意：这里不做“业务延迟”，而是为了等待 Dropdown 的浮层/遮罩在同一事件循环结束后完成销毁。
                        // 若在 close 之后立刻同步触发 onEdit，Modal 可能会在遮罩尚未完全移除的那一帧被盖住，出现点不到/闪现问题。
                        // setTimeout 是一次性（delay=0）的调度，不属于 setInterval 之类可持续副作用，因此无需清理。
                        window.setTimeout(() => {
                          onEdit(String(item?.value ?? ''));
                        }, 0);
                      }}
                    >
                      <SvgIcon type="pencil-m" />
                    </span>
                  </div>
                </List.Item>
              );
            })}
          </List>
        </div>
      )}
    </Dropdown>
    </div>
  );
};

export default memo(AiAnalysisApproachIcon);
